import {createStyles} from '@mantine/core';
import React from 'react';

import {TaskSchemaNodeRowData} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

export interface AddFieldToSectionButtonProps {
  /** The list of TaskSchemaNodeRowData to display in the dropdown */
  rows: Array<TaskSchemaNodeRowData>;
  /** Callback function called when a row is selected */
  onAddFieldToSection: (rowData: TaskSchemaNodeRowData) => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Button icon */
  icon: BenchlingIconName;
  /** Tooltip */
  tooltip?: string;
  /** Button size */
  size?: ButtonSize;
  /** Button style */
  buttonStyle?: ButtonStyle;
  /** Optional className for the button */
  className?: string;
}

/**
 * A button component that displays a dropdown list of TaskSchemaNodeRowData when clicked.
 * When a row is selected, the onAddFieldToSection callback is called with the selected row data.
 *
 * @example
 * ```tsx
 * <TaskSchemaFieldSelectorButton
 *   rows={sampleRows}
 *   onAddFieldToSection={handleFieldSelection}
 *   icon="plus"
 *   size={ButtonSize.MEDIUM}
 *   buttonStyle={ButtonStyle.SECONDARY}
 * />
 * ```
 */
export default function AddFieldToSectionButton({
  rows,
  onAddFieldToSection,
  disabled = false,
  icon,
  tooltip,
  size = ButtonSize.SMALL,
  buttonStyle = ButtonStyle.TEXT,
  className,
}: AddFieldToSectionButtonProps): JSX.Element {
  const handleRowSelect = (rowData: TaskSchemaNodeRowData): void => {
    onAddFieldToSection(rowData);
  };
  const {classes} = useStyles();

  const menu = (
    <Menu scrollable={true} width={Menu.WIDTHS.MEDIUM} height={Menu.HEIGHTS.MEDIUM}>
      {rows.map((row) => (
        <Option
          key={row.id}
          value={row}
          onSelect={handleRowSelect}
          icon={row.fieldIcon}
          metadata={row.fieldTypeLabel}
          dataTestAttrs={{
            component: 'AddFieldToSectionButton',
            element: 'option',
            key: row.fieldName,
          }}
        >
          {row.fieldName}
        </Option>
      ))}
      {rows.length === 0 && (
        <Option
          value={null}
          isDisabled={true}
          dataTestAttrs={{
            component: 'AddFieldToSectionButton',
            element: 'emptyOption',
          }}
        >
          No fields available
        </Option>
      )}
    </Menu>
  );

  return (
    <Dropdown
      menu={menu}
      className={className}
      closeOnInsideClick={true}
      closeOnOutsideClick={true}
      tooltip={tooltip}
      alwaysDropDown={true}
    >
      <Button
        buttonStyle={buttonStyle}
        className={classes['button--icon']}
        size={size}
        icon={icon}
        disabled={disabled}
        dataTestAttrs={{
          component: 'AddFieldToSectionButton',
          element: 'button',
        }}
      />
    </Dropdown>
  );
}

const useStyles = createStyles(() => ({
  'button--icon': {
    padding: '2px !important', // Override the default button padding to fit in the section header
    height: 'unset !important', // Override the default button height to fit in the section header
  },
}));
